'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Palette, Contrast, Droplet } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomColorGeneratorContent } from '../../data/random-color-generator-content';
import Link from 'next/link';

type ColorFormat = 'hex' | 'rgb' | 'hsl';

interface Color {
    hex: string;
    rgb: string;
    hsl: string;
    contrastColor: string;
}

const generateRandomColor = (format: ColorFormat = 'hex'): Color => {
    // Generate random RGB values
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);

    // Calculate HSL values
    const [h, s, l] = rgbToHsl(r, g, b);

    // Format strings
    const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
    const rgb = `rgb(${r}, ${g}, ${b})`;
    const hsl = `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;

    // Calculate contrast color (black or white) for text visibility
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    const contrastColor = brightness > 128 ? '#000000' : '#FFFFFF';

    return { hex, rgb, hsl, contrastColor };
};

// Helper function to convert RGB to HSL
const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }

        h /= 6;
    }

    return [h * 360, s * 100, l * 100];
};

const formatColor = (color: Color, format: ColorFormat): string => {
    switch (format) {
        case 'hex': return color.hex;
        case 'rgb': return color.rgb;
        case 'hsl': return color.hsl;
        default: return color.hex;
    }
};

export default function RandomColorGenerator() {
    const [color, setColor] = useState<Color>(() => generateRandomColor());
    const [format, setFormat] = useState<ColorFormat>('hex');
    const [copied, setCopied] = useState<boolean>(false);
    const [hueRange, setHueRange] = useState<[number, number]>([0, 360]);
    const [saturationRange, setSaturationRange] = useState<[number, number]>([20, 100]);
    const [lightnessRange, setLightnessRange] = useState<[number, number]>([20, 80]);
    const [useCustomRange, setUseCustomRange] = useState<boolean>(false);
    const { toast } = useToast();

    const generateColor = useCallback(() => {
        let newColor: Color;

        if (useCustomRange) {
            // Keep generating until we get a color within the specified ranges
            let attempts = 0;
            const maxAttempts = 100;

            do {
                newColor = generateRandomColor();
                const hsl = newColor.hsl.match(/\d+/g)?.map(Number) || [0, 0, 0];
                const [h, s, l] = hsl;

                if (h >= hueRange[0] && h <= hueRange[1] &&
                    s >= saturationRange[0] && s <= saturationRange[1] &&
                    l >= lightnessRange[0] && l <= lightnessRange[1]) {
                    break;
                }

                attempts++;
            } while (attempts < maxAttempts);

            // If we couldn't find a matching color after max attempts, use the last one
            if (attempts >= maxAttempts) {
                toast({
                    title: 'Warning',
                    description: 'Could not generate a color within the specified ranges. Try adjusting your ranges.',
                    variant: 'destructive',
                });
            }
        } else {
            newColor = generateRandomColor();
        }

        setColor(newColor);
    }, [hueRange, saturationRange, lightnessRange, useCustomRange, toast]);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(formatColor(color, format));
        setCopied(true);
        toast({
            title: 'Copied!',
            description: `${format.toUpperCase()} color has been copied to clipboard.`,
        });

        setTimeout(() => setCopied(false), 2000);
    }, [color, format, toast]);

    // Generate initial color on mount
    useEffect(() => {
        generateColor();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="mb-8 text-center">
                    <ToolPageIntro title="Random Color Generator">
                Generate random colors with our free online tool. Create hex codes, RGB values, and color palettes for design, development, and creative projects.
            </ToolPageIntro>
                </div>

                <div className="grid gap-6">
                    {/* Color Display */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Color Preview</CardTitle>
                            <CardDescription>
                                {formatColor(color, format)}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div
                                className="w-full h-48 rounded-lg flex items-center justify-center transition-colors duration-300"
                                style={{
                                    backgroundColor: color.hex,
                                    color: color.contrastColor
                                }}
                            >
                                <div className="text-center p-4 bg-black/20 rounded-lg">
                                    <p className="text-lg font-mono font-bold">{formatColor(color, format)}</p>
                                    <p className="text-sm opacity-80">Click to copy</p>
                                </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setFormat('hex')}
                                    className={format === 'hex' ? 'bg-accent' : ''}
                                >
                                    HEX
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setFormat('rgb')}
                                    className={format === 'rgb' ? 'bg-accent' : ''}
                                >
                                    RGB
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setFormat('hsl')}
                                    className={format === 'hsl' ? 'bg-accent' : ''}
                                >
                                    HSL
                                </Button>
                                <div className="flex-1" />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Color Controls */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Color Settings</CardTitle>
                            <CardDescription>
                                Customize the color generation
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <Label>Color Range</Label>
                                    <p className="text-sm text-muted-foreground">
                                        {useCustomRange ? 'Custom range enabled' : 'Using full color spectrum'}
                                    </p>
                                </div>
                                <Switch
                                    checked={useCustomRange}
                                    onCheckedChange={setUseCustomRange}
                                />
                            </div>

                            {useCustomRange && (
                                <div className="space-y-6">
                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <Label>Hue: {hueRange[0]}° - {hueRange[1]}°</Label>
                                        </div>
                                        <Slider
                                            value={hueRange}
                                            onValueChange={(value: number[]) => setHueRange([value[0], value[1]])}
                                            min={0}
                                            max={360}
                                            step={1}
                                            minStepsBetweenThumbs={10}
                                            className="py-4"
                                        />
                                        <div className="h-4 w-full rounded-full overflow-hidden">
                                            <div
                                                className="h-full w-full"
                                                style={{
                                                    background: 'linear-gradient(90deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <Label>Saturation: {saturationRange[0]}% - {saturationRange[1]}%</Label>
                                        </div>
                                        <Slider
                                            value={saturationRange}
                                            onValueChange={(value: number[]) => setSaturationRange([value[0], value[1]])}
                                            min={0}
                                            max={100}
                                            step={1}
                                            minStepsBetweenThumbs={5}
                                            className="py-4"
                                        />
                                    </div>

                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <Label>Lightness: {lightnessRange[0]}% - {lightnessRange[1]}%</Label>
                                        </div>
                                        <Slider
                                            value={lightnessRange}
                                            onValueChange={(value: number[]) => setLightnessRange([value[0], value[1]])}
                                            min={0}
                                            max={100}
                                            step={1}
                                            minStepsBetweenThumbs={5}
                                            className="py-4"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="pt-2">
                                <Button
                                    onClick={generateColor}
                                    className="w-full"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate New Color
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Color Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Color Information</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="flex items-center"><Droplet className="h-4 w-4 mr-2" /> HEX</Label>
                                    <div className="p-3 rounded-md bg-muted font-mono text-sm">
                                        {color.hex}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center"><Droplet className="h-4 w-4 mr-2" /> RGB</Label>
                                    <div className="p-3 rounded-md bg-muted font-mono text-sm">
                                        {color.rgb}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center"><Droplet className="h-4 w-4 mr-2" /> HSL</Label>
                                    <div className="p-3 rounded-md bg-muted font-mono text-sm">
                                        {color.hsl}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>
                        This tool generates random colors using the Web Crypto API.
                        All processing happens in your browser - no data is sent to any server.
                    </p>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomColorGeneratorContent.title}
                description={randomColorGeneratorContent.description}
                content={randomColorGeneratorContent.content}
                faqs={randomColorGeneratorContent.faqs}
                relatedTools={randomColorGeneratorContent.relatedTools}
            />
        </div>
    );
}
